import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPatients } from '../../../Redux/PatientSlice';
import { hasPermission } from '../../../Redux/permissionSlice';
import { selectRoles } from '../../../Redux/AuthSlice';
import { BsEye, BsPencil } from 'react-icons/bs';
import Table from '../../dataUi/Table';
import { InfinitySpin } from 'react-loader-spinner';
import Breadcrumb from '../../dataUi/Breadcrumb';
import UpdatePatientModal from './UpdatePatientModal';

const PatientsInfo = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id, branch_id } = useSelector((state) => state.auth);
    const { patients, loading, error } = useSelector((state) => state.patients);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);

    useEffect(() => {
        dispatch(fetchPatients({
            userId: id,
            branchId: branch_id,
        }));
    }, [dispatch, id, branch_id]);

    const selectedRoles = useSelector(selectRoles);
    const roles = Array.isArray(selectedRoles) ? selectedRoles : selectedRoles.split(',').map(role => role.trim());

    // Table configuration
    const headers = [
        { key: 'no', label: 'No.', sortable: false },
        // { key: 'id', label: 'ID', sortable: true },
        { key: 'first_name', label: 'First Name', sortable: true },
        { key: 'last_name', label: 'Last Name', sortable: true },
        { key: 'date_of_birth', label: 'Date of Birth', sortable: true },
        { key: 'id_type', label: 'ID Type', sortable: true },
        { key: 'id_number', label: 'ID Number', sortable: true },
    ];

    const actions = [
        {
            label: 'View',
            variant: 'primary',
            icon: <BsEye />,
            onClick: (row) => {
                navigate(`/retail/prescriptions/view-patient/${row.id}`, {
                    state: { requiredRoles: ['prescriptionViewer', 'prescriptionWriter', 'prescriptionAdmin'] }
                });
            }
        },
        {
            label: 'Edit',
            variant: 'secondary',
            icon: <BsPencil />,
            onClick: (row) => {
                setSelectedPatient(row);
                setIsUpdateModalOpen(true);
            },
            show: hasPermission(roles, ['prescriptionWriter', 'prescriptionAdmin']),
            className: "bg-green-500 hover:bg-green-800 text-white"
        }
    ];

    const tableRows = patients.map((patient, index) => ({
        no: index + 1,
        ...patient,
        date_of_birth: new Date(patient.date_of_birth).toLocaleDateString()
    }));

    const breadcrumbItems = [
        {
            label: "Prescriptions",
            onClick: () => navigate("/retail/prescriptions"),
        },
        {
            label: 'Patients info'
        }
    ];

    return (
        <div className='absolute w-full h-full font-lufga'>
            <div>
                {loading && (
                    <div className="absolute top-0 left-0 flex items-center justify-center w-full h-full bg-gradient-to-br from-opacity-40 to-opacity-60 backdrop-blur-sm">
                        <InfinitySpin color="#059212" loading={loading} size={40} />
                    </div>
                )}
                {!loading && (
                    <>
                        <Breadcrumb items={breadcrumbItems} className='flex items-center mx-6 mb-3 text-sm text-green-500 transition-all duration-300 hover:text-green-600' />
                        <div className="mx-6 font-lufga">
                            <Table
                                headers={headers}
                                rows={tableRows}
                                actions={actions}
                                title="Patients"
                                itemsPerPage={15}
                                emptyMessage={error ? "Error loading patients" : "No patients found"}
                                headerBgColor="bg-green-200"
                                onRowClick={(row) => navigate(`/retail/prescriptions/view-patient/${row.id}`, {
                                    state: { requiredRoles: ['prescriptionViewer', 'prescriptionWriter', 'prescriptionAdmin'] }
                                })}
                                showSearch={true}
                                searchPlaceholder="Search by name or ID..."
                                onSearch={(query) => {
                                    const searchTerm = query.toLowerCase().trim();
                                    const filteredPatients = patients.filter(patient => {
                                        const fullName = `${patient.first_name} ${patient.last_name}`.toLowerCase();
                                        const idNumber = patient.id_number?.toLowerCase() || '';
                                        return fullName.includes(searchTerm) ||
                                            idNumber.includes(searchTerm) ||
                                            patient.first_name.toLowerCase().includes(searchTerm) ||
                                            patient.last_name.toLowerCase().includes(searchTerm);
                                    });
                                    return filteredPatients.map(patient => ({
                                        ...patient,
                                        date_of_birth: new Date(patient.date_of_birth).toLocaleDateString()
                                    }));
                                }}
                                showDateFilter={true}
                                onDateChange={({ startDate, endDate }) => {
                                    if (!startDate || !endDate) return tableRows;
                                    return patients.filter(patient => {
                                        const patientDate = new Date(patient.date_of_birth);
                                        return patientDate >= new Date(startDate) && patientDate <= new Date(endDate);
                                    }).map(patient => ({
                                        ...patient,
                                        date_of_birth: new Date(patient.date_of_birth).toLocaleDateString()
                                    }));
                                }}
                                dateFilterProps={{
                                    placeholder: 'Filter by date of birth',
                                    minDate: '1900-01-01',
                                    maxDate: new Date().toISOString().split('T')[0],
                                }}
                                addButtonText="Add Patient"
                                onAdd={() => navigate('/retail/prescriptions/add-patients', {
                                    state: { requiredRoles: ['prescriptionViewer', 'prescriptionWriter', 'prescriptionAdmin'] }
                                })}
                                showAddButton={hasPermission(roles, ['prescriptionWriter', 'prescriptionAdmin'])}
                                addButtonClassName="bg-green-500 hover:bg-green-600 text-white"
                            />
                        </div>
                    </>
                )}
            </div>

            <UpdatePatientModal
                isOpen={isUpdateModalOpen}
                onClose={() => {
                    setIsUpdateModalOpen(false);
                    setSelectedPatient(null);
                }}
                patientToEdit={selectedPatient}
            />
        </div>
    );
};

export default PatientsInfo;